import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

// Components
import { Header } from '@/components/Header';
import { Sidebar } from '@/components/Sidebar';
import { EditorPane } from '@/components/EditorPane';
import { TerminalPane } from '@/components/TerminalPane';
import { ChatPane } from '@/components/ChatPane';
import { VisualizerPane } from '@/components/VisualizerPane';
import { StudentCard } from '@/components/StudentCard';
import { XPPopup, AchievementPopup } from '@/components/GamificationPopups';

// Hooks
import { usePyodide } from '@/hooks/usePyodide';
import { useChat } from '@/hooks/useChat';
import { useTheme } from '@/hooks/useTheme';
import { useGamification } from '@/hooks/useGamification';
import { useAuth } from '@/hooks/useAuth';
import { useCodeExplanation, useRealTimeSuggestions } from '@/hooks/useCodeExplanation';

// Icons
import { Eye, MessageCircle, Menu, User, Code2, Terminal } from 'lucide-react';

const defaultCode = `# ברוכים הבאים ל-PyZone! 🐍
# Welcome to PyZone!

# בואו נתחיל עם תוכנית פשוטה
name = "עולם"
print(f"שלום {name}!")

# לולאה שמדפיסה מספרים
for i in range(5):
    print(f"מספר {i}")
`;

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const PyZoneApp = () => {
  // State
  const [code, setCode] = useState(defaultCode);
  const [activeTab, setActiveTab] = useState('chat');
  const [mobileView, setMobileView] = useState('editor');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const isMobile = useIsMobile();
  
  // Hooks
  const { theme, setTheme, themes, currentTheme } = useTheme();
  const { 
    user, 
    loading: authLoading, 
    signIn, 
    signOut, 
    isAuthenticated 
  } = useAuth();
  const {
    enabled: gamificationEnabled,
    xp,
    level,
    streak,
    achievements,
    levelProgress,
    recentXP,
    newAchievement,
    addXP,
    unlockAchievement,
    updateStreak,
    toggleGamification,
  } = useGamification();
  const {
    output,
    isRunning,
    runCode,
    clearOutput,
    pendingInput,
    submitInput,
    loading: pyodideLoading,
    // Debug features
    isDebugging,
    isPreparingDebug,
    debugSteps,
    currentStepIdx,
    currentLine,
    variables,
    startDebug,
    stepForward,
    stepBackward,
    stopDebug,
    runToEnd,
  } = usePyodide();
  const {
    messages,
    isLoading: chatLoading,
    sendMessage,
    clearChat,
  } = useChat();
  const {
    explanations,
    fullExplanation,
    isLoading: explanationLoading,
    getLineExplanations,
    getFullExplanation,
    clearExplanations,
  } = useCodeExplanation();

  // Real-time AI suggestions (12s debounce)
  const suggestion = useRealTimeSuggestions(code, isDebugging);

  // Handlers
  const handleRunCode = useCallback(() => {
    runCode(code);
    updateStreak();
    clearExplanations();
    
    if (isMobile) {
      setMobileView('terminal');
    }
    
    if (gamificationEnabled) {
      addXP(5, 'הרצת קוד');
      
      if (!achievements.includes('first_run')) {
        unlockAchievement('first_run');
      }
      
      if (code.includes('print') && code.includes('שלום') && !achievements.includes('hello_world')) {
        unlockAchievement('hello_world');
      }
      
      if ((code.includes('for ') || code.includes('while ')) && !achievements.includes('loop_master')) {
        unlockAchievement('loop_master');
      }
    }
  }, [code, runCode, updateStreak, gamificationEnabled, addXP, achievements, unlockAchievement, isMobile, clearExplanations]);

  const handleClearCode = useCallback(() => {
    setCode(defaultCode);
    clearExplanations();
  }, [clearExplanations]);

  const handleSendMessage = useCallback((message, codeContext) => {
    sendMessage(message, codeContext);
    
    if (gamificationEnabled) {
      addXP(2, 'שאלת שאלה');
      
      const userMessages = messages.filter(m => m.role === 'user').length;
      if (userMessages >= 4 && !achievements.includes('chat_friend')) {
        unlockAchievement('chat_friend');
      }
    }
  }, [sendMessage, gamificationEnabled, addXP, messages, achievements, unlockAchievement]);

  // Debug handlers
  const handleStartDebug = useCallback(async () => {
    // Fetch explanations in parallel with trace
    getLineExplanations(code);
    await startDebug(code);
    
    // Switch to visualizer tab
    setActiveTab('visualizer');
    
    if (gamificationEnabled) {
      addXP(3, 'התחלת דיבוג');
    }
  }, [code, getLineExplanations, startDebug, gamificationEnabled, addXP]);

  const handleStepForward = useCallback(() => {
    stepForward();
    
    if (gamificationEnabled && currentStepIdx === 0) {
      addXP(1, 'צעד בדיבוג');
    }
  }, [stepForward, gamificationEnabled, currentStepIdx, addXP]);

  const handleStepBackward = useCallback(() => {
    stepBackward();
  }, [stepBackward]);

  const handleStopDebug = useCallback(() => {
    stopDebug();
    clearExplanations();
  }, [stopDebug, clearExplanations]);

  const handleExplainCode = useCallback(async () => {
    setActiveTab('visualizer');
    await getFullExplanation(code);
  }, [code, getFullExplanation]);

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="h-screen flex flex-col bg-background overflow-hidden" dir="rtl">
        <Header
          currentTheme={currentTheme}
          themes={themes}
          onThemeChange={setTheme}
          gamificationEnabled={gamificationEnabled}
          onToggleGamification={toggleGamification}
          suggestion={suggestion}
        />

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-2 py-2 border-b border-border/30 bg-card/50">
            <div className="flex gap-1">
              <Button
                variant={mobileView === 'editor' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setMobileView('editor')}
                className="h-8 px-3"
              >
                <Code2 className="h-4 w-4 ml-1" />
                קוד
              </Button>
              <Button
                variant={mobileView === 'terminal' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setMobileView('terminal')}
                className="h-8 px-3"
              >
                <Terminal className="h-4 w-4 ml-1" />
                פלט
              </Button>
              <Button
                variant={mobileView === 'assistant' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setMobileView('assistant')}
                className="h-8 px-3"
              >
                <MessageCircle className="h-4 w-4 ml-1" />
                חונך
              </Button>
            </div>
            
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2">
                  <User className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-4 overflow-auto">
                <div className="space-y-4">
                  <StudentCard
                    user={user}
                    isAuthenticated={isAuthenticated}
                    onSignIn={signIn}
                    onSignOut={signOut}
                    loading={authLoading}
                    gamification={{
                      enabled: gamificationEnabled,
                      xp,
                      level,
                      streak,
                      achievements,
                      levelProgress,
                    }}
                  />
                  <Sidebar gamificationEnabled={gamificationEnabled} />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex-1 overflow-hidden p-2">
            {mobileView === 'editor' && (
              <EditorPane
                code={code}
                onCodeChange={setCode}
                onRun={handleRunCode}
                isRunning={isRunning || pyodideLoading}
                onClear={handleClearCode}
                currentLine={isDebugging ? currentLine : 0}
              />
            )}
            
            {mobileView === 'terminal' && (
              <TerminalPane
                output={output}
                onClear={clearOutput}
                pendingInput={pendingInput}
                onSubmitInput={submitInput}
                loading={isRunning}
              />
            )}
            
            {mobileView === 'assistant' && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                <TabsList className="grid w-full grid-cols-2 mb-2">
                  <TabsTrigger value="chat" className="text-sm">
                    <MessageCircle className="h-3 w-3 ml-1" />
                    החונך שלי
                  </TabsTrigger>
                  <TabsTrigger value="visualizer" className="text-sm">
                    <Eye className="h-3 w-3 ml-1" />
                    מבט פנימי
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="chat" className="flex-1 mt-0 overflow-hidden">
                  <ChatPane
                    messages={messages}
                    isLoading={chatLoading}
                    onSendMessage={handleSendMessage}
                    onClear={clearChat}
                    currentCode={code}
                  />
                </TabsContent>
                
                <TabsContent value="visualizer" className="flex-1 mt-0 overflow-hidden">
                  <VisualizerPane
                    isDebugging={isDebugging}
                    isPreparingDebug={isPreparingDebug}
                    currentLine={currentLine}
                    variables={variables}
                    explanations={explanations}
                    fullExplanation={fullExplanation}
                    explanationLoading={explanationLoading}
                    debugSteps={debugSteps}
                    currentStepIdx={currentStepIdx}
                    onStartDebug={handleStartDebug}
                    onExplainCode={handleExplainCode}
                    onStepForward={handleStepForward}
                    onStepBackward={handleStepBackward}
                    onStopDebug={handleStopDebug}
                    onRunToEnd={runToEnd}
                  />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>

        <XPPopup xp={recentXP} />
        <AchievementPopup achievement={newAchievement} />
        <Toaster position="top-center" dir="rtl" />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden" dir="rtl">
      <Header
        currentTheme={currentTheme}
        themes={themes}
        onThemeChange={setTheme}
        gamificationEnabled={gamificationEnabled}
        onToggleGamification={toggleGamification}
        suggestion={suggestion}
      />

      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Right Panel - Dashboard */}
          <ResizablePanel defaultSize={20} minSize={15} maxSize={30}>
            <div className="h-full p-4 overflow-auto custom-scrollbar">
              <div className="space-y-4">
                <StudentCard
                  user={user}
                  isAuthenticated={isAuthenticated}
                  onSignIn={signIn}
                  onSignOut={signOut}
                  loading={authLoading}
                  gamification={{
                    enabled: gamificationEnabled,
                    xp,
                    level,
                    streak,
                    achievements,
                    levelProgress,
                  }}
                />
                <Sidebar gamificationEnabled={gamificationEnabled} />
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle className="bg-border/30 hover:bg-primary/30 transition-colors" />

          {/* Center Panel - Workspace */}
          <ResizablePanel defaultSize={50} minSize={35}>
            <div className="h-full p-4">
              <ResizablePanelGroup direction="vertical" className="h-full">
                <ResizablePanel defaultSize={70} minSize={40}>
                  <EditorPane
                    code={code}
                    onCodeChange={setCode}
                    onRun={handleRunCode}
                    isRunning={isRunning || pyodideLoading}
                    onClear={handleClearCode}
                    currentLine={isDebugging ? currentLine : 0}
                  />
                </ResizablePanel>

                <ResizableHandle withHandle className="bg-border/30 hover:bg-primary/30 transition-colors my-2" />

                <ResizablePanel defaultSize={30} minSize={20}>
                  <TerminalPane
                    output={output}
                    onClear={clearOutput}
                    pendingInput={pendingInput}
                    onSubmitInput={submitInput}
                    loading={isRunning}
                  />
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle className="bg-border/30 hover:bg-primary/30 transition-colors" />

          {/* Left Panel - Assistants */}
          <ResizablePanel defaultSize={30} minSize={20} maxSize={40}>
            <div className="h-full p-4">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="chat" className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    <span>החונך שלי</span>
                  </TabsTrigger>
                  <TabsTrigger value="visualizer" className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    <span>מבט פנימי</span>
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="chat" className="flex-1 mt-0 overflow-hidden">
                  <ChatPane
                    messages={messages}
                    isLoading={chatLoading}
                    onSendMessage={handleSendMessage}
                    onClear={clearChat}
                    currentCode={code}
                  />
                </TabsContent>
                
                <TabsContent value="visualizer" className="flex-1 mt-0 overflow-hidden">
                  <VisualizerPane
                    isDebugging={isDebugging}
                    isPreparingDebug={isPreparingDebug}
                    currentLine={currentLine}
                    variables={variables}
                    explanations={explanations}
                    fullExplanation={fullExplanation}
                    explanationLoading={explanationLoading}
                    debugSteps={debugSteps}
                    currentStepIdx={currentStepIdx}
                    onStartDebug={handleStartDebug}
                    onExplainCode={handleExplainCode}
                    onStepForward={handleStepForward}
                    onStepBackward={handleStepBackward}
                    onStopDebug={handleStopDebug}
                    onRunToEnd={runToEnd}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      <XPPopup xp={recentXP} />
      <AchievementPopup achievement={newAchievement} />
      <Toaster position="top-center" dir="rtl" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PyZoneApp />} />
        <Route path="*" element={<PyZoneApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
